/**
 * Login page with email/password and Google OAuth
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { useAuthStore, useLanguage } from '@/store';
import { getAuthTranslation, getAuthErrorTranslation } from '@/utils/translations';
import { googleRedirectErrorKey } from '@/utils/authErrors';
import { safeRedirect, withRedirect } from '@/utils/redirect';
import { ROUTES } from '@/config/routes';

const ERROR_ID = 'login-error';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const language = useLanguage();
  const { login, loginWithGoogle, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // A5: back to the page that sent the visitor here (validated, same origin), else Početna.
  const redirect = safeRedirect(searchParams.get('redirect'));
  // The Google callback returns here with ?error= when sign-in failed.
  const [googleError, setGoogleError] = useState(() => googleRedirectErrorKey(searchParams.get('error')));
  const shownError = error ?? googleError;
  const emailConfirmParam = searchParams.get('email_confirmed');
  const emailConfirmStatus: 'success' | 'expired' | 'invalid' | null =
    emailConfirmParam === 'success' || emailConfirmParam === 'expired' || emailConfirmParam === 'invalid'
      ? emailConfirmParam
      : null;

  // Helper function for translations
  const t = (key: Parameters<typeof getAuthTranslation>[0]) => getAuthTranslation(key, language);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setGoogleError(null);

    const trimmedEmail = email.trim();
    const success = await login(trimmedEmail, password);
    if (success) {
      navigate(redirect ?? ROUTES.home);
    }
  };

  const handleGoogleLogin = async () => {
    clearError();
    setGoogleError(null);
    await loginWithGoogle(redirect);
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {t('welcomeBack')}
          </CardTitle>
          <CardDescription>
            {t('signInToContinue')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailConfirmStatus && (
            <div role="status" className={`mb-4 p-3 rounded-md text-sm flex items-start gap-2 ${
              emailConfirmStatus === 'success'
                ? 'bg-green-500/10 text-green-500'
                : emailConfirmStatus === 'expired'
                ? 'bg-yellow-500/10 text-yellow-500'
                : 'bg-destructive/10 text-destructive'
            }`}>
              {emailConfirmStatus === 'success' ? (
                <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
              ) : emailConfirmStatus === 'expired' ? (
                <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              )}
              <span>
                {emailConfirmStatus === 'success'
                  ? t('emailConfirmed')
                  : emailConfirmStatus === 'expired'
                  ? t('emailConfirmExpired')
                  : t('emailConfirmInvalid')}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" aria-describedby={shownError ? ERROR_ID : undefined}>
            {shownError && (
              <div
                id={ERROR_ID}
                role="alert"
                className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
              >
                {getAuthErrorTranslation(shownError, language)}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                {t('email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('enterYourEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="email"
                  autoFocus
                  aria-invalid={!!shownError}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                {t('password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('enterYourPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-11"
                  required
                  autoComplete="current-password"
                  aria-invalid={!!shownError}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                  aria-pressed={showPassword}
                  className="absolute right-0 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                {t('forgotPassword')}
              </Link>
            </div>

            <Button type="submit" className="w-full" isLoading={isLoading}>
              {t('signIn')}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('orContinueWith')}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </Button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t('dontHaveAccount')}{' '}
            <Link to={withRedirect(ROUTES.register, redirect)} className="font-medium text-primary hover:underline">
              {t('signUp')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
